import { makeStyles } from '@material-ui/core/styles';
import { useState } from 'react';
import { Store, useAppDispatch } from 'src/redux/Store';
import { useSelector } from 'react-redux';
import Button from '@material-ui/core/Button';
import { t } from 'src/lib/language';
import SubmenuLink from 'src/components/UI/SubmenuLink';
import { closeModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import { FundraisingTypes } from 'src/constants/FundraisingTypes';
import FundraisingInformationForm
  from 'src/components/Forms/Fundraising/FundraisingInformationForm';
import {
  FundraisingInfo,
  FundraisingMatchingInfo,
  FundraisingNotification,
  FundraisingProductsInfo,
} from 'src/models/FundraisingModel';
import FundraisingNotificationForm
  from 'src/components/Forms/Fundraising/FundraisingNotificationForm';
import FundraisingProductdForm from 'src/components/Forms/Fundraising/FundraisingProductsForm';
import moment from 'moment-timezone-all';
import { openDialog } from 'src/redux/actions/dialogActions';
import { DIALOGS } from 'src/models/DialogModel';
import {
  postFundraisingsAction,
  putFundraisingsAction,
} from 'src/redux/actions/fundraisingAction';
import palette from 'src/styles/palette';
import Title from 'src/components/UI/Title';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    maxWidth: '640px',
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  formControl: {
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  selectElement: {
    width: '100%',
  },
  selectLabel: {
    color: 'rgba(0, 0, 0, 0.50)',
  },
  selectLabelError: {
    color: 'rgba(255, 0, 0, 0.50)',
  },
  spacer: {
    height: theme.spacing(1),
  },
  hide: {
    display: 'none',
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'top',
    gap: '10px',
    marginTop: '10px',
  },
  header: {
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(1),
    height: '54px',
  },
  title: {
    color: palette.primary.contrastText,
    backgroundColor: palette.primary.light,
    padding: '8px',
    fontSize: '18px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  scroller: {
    overflowY: 'auto',
    height: '66vh',
  },
}));

export const enum TABS {
  info = 'info',
  products = 'products',
  notifications = 'notifications',
  assets = 'assets',
  ambassador = 'ambassador',
}

type ErrorState = {
  [key in TABS]: boolean
};

const AddFundraisingPage = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const lang = useSelector((state: Store) => state.language.language);

  const [currentTab, setCurrentTab] = useState(TABS.info);
  const [step, setStep] = useState(1);
  const [hasErrors, setHasErrors] = useState({} as ErrorState);
  const [saved, setSaved] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  const [informationState, setInformationState] = useState({
    active: true,
    show_countdown: false,
    type: FundraisingTypes.standard,
    start_date: moment().startOf('day'),
    end_date: moment().add(1, 'week').endOf('day'),
  } as FundraisingInfo);
  const [matchingInfoState, setMatchingInfoState] = useState({} as FundraisingMatchingInfo);
  const [notificationState, setNotificationState] = useState({
    contacts: [] as number[],
    groups: [] as number[],
    send_thanks_email: false,
    send_thanks_sms: false,
    thanks_email_body: '',
  } as FundraisingNotification);
  const [productState, setProductState] = useState({} as FundraisingProductsInfo);

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.addEvent }));
    setStep(1);
  };

  const transformData = () => {
    const productsData = {
      ...productState,
      products: JSON.stringify(productState.products),
    };
    return {
      ...informationState,
      ...notificationState,
      ...productsData,
    };
  };

  const saveFundraising = () => {
    if (saved) {
      dispatch(putFundraisingsAction(transformData(), setSaved));
      return;
    }
    dispatch(postFundraisingsAction(transformData(), setSaved));
  };

  const setTabError = (tab: TABS, hasError: boolean) => {
    setHasErrors({
      ...hasErrors,
      [tab]: hasError,
    });
  };

  const changeTabCallback = (tab: TABS) => {
    setHasChanged(false);
    setCurrentTab(tab);
  };

  const changeTab = (tab: TABS) => {
    if (hasChanged) {
      dispatch(openDialog(
        { dialog: DIALOGS.leaveTab, payload: { callback: () => changeTabCallback(tab) } },
      ));
      return;
    }
    setCurrentTab(tab);
  };

  const onNextStep = (newStep: number) => {
    if (saved) {
      saveFundraising();
      return;
    }
    if (newStep > step) {
      setStep(newStep);
    }
    switch (newStep) {
      case 2:
        setCurrentTab(TABS.products);
        break;
      case 3:
        setCurrentTab(TABS.assets);
        break;
      case 4:
        setCurrentTab(TABS.ambassador);
        break;
      case 5:
        setCurrentTab(TABS.notifications);
        break;
      default:
        break;
    }
  };

  const displayForm = () => {
    switch (currentTab) {
      case TABS.products:
        return (
          <FundraisingProductdForm
            onChange={setProductState}
            onNextStep={onNextStep}
            setErrors={setTabError}
            initialState={productState}
            showSuggested={informationState.show_suggested}
            askForFees={informationState.ask_for_fee}
            setHasChanged={setHasChanged} />
        );
      case TABS.notifications:
        return (
          <FundraisingNotificationForm
            onChange={setNotificationState}
            onNextStep={onNextStep}
            setErrors={setTabError}
            slug={informationState.slug}
            initialState={notificationState}
            setHasChanged={setHasChanged} />
        );
      default:
        return (
          <FundraisingInformationForm
            setInformationValue={setInformationState}
            setMatchingInfoValue={setMatchingInfoState}
            onNextStep={onNextStep}
            setErrors={setTabError}
            information={informationState}
            matchingInfo={matchingInfoState}
            setHasChanged={setHasChanged} />
        );
    }
  };

  return (
    <>
      <div className={classes.header}>
        <div className={classes.row}>
          <Title>Add a fundraising</Title>
          <Button
            style={{ marginLeft: 'auto', marginBottom: '0px' }}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClose}>
            {t(lang, 'misc.close')}
          </Button>
        </div>
      </div>
      <div className={classes.row}>
        <SubmenuLink
          onClick={() => changeTab(TABS.info)}
          selected={currentTab === TABS.info}
          hasError={hasErrors[TABS.info]}
          color="#0A4DF2">
          Informations
        </SubmenuLink>
        <SubmenuLink
          onClick={() => changeTab(TABS.products)}
          selected={currentTab === TABS.products}
          disabled={(step < 2) || !informationState.slug}
          color="#0A4DF2"
          hasError={hasErrors[TABS.products]}>
          Products
        </SubmenuLink>
        <SubmenuLink
          onClick={() => changeTab(TABS.assets)}
          selected={currentTab === TABS.assets}
          disabled={(step < 3) || !informationState.slug}
          color="#0A4DF2"
          hasError={hasErrors[TABS.assets]}>
          Assets
        </SubmenuLink>
        <SubmenuLink
          onClick={() => changeTab(TABS.ambassador)}
          selected={currentTab === TABS.ambassador}
          disabled={(step < 4) || !informationState.slug}
          color="#0A4DF2"
          hasError={hasErrors[TABS.ambassador]}>
          Ambassador
        </SubmenuLink>
        <SubmenuLink
          onClick={() => changeTab(TABS.notifications)}
          selected={currentTab === TABS.notifications}
          disabled={(step < 5) || !informationState.slug}
          color="#0A4DF2"
          hasError={hasErrors[TABS.notifications]}>
          Notifications/Social
        </SubmenuLink>
      </div>
      <div className={classes.scroller}>
        {displayForm()}
      </div>
    </>
  );
};

export default AddFundraisingPage;
